import { useToast } from "vue-toastification";
import { useAuthStore } from "@/stores/auth";

const TOKEN_RELATED_ERROR = [
  "Missing Access Token",
  "Access Token Expired",
  "Invalid Access Token",
];
const REFRESH_ENDPOINT = "/api/auth/refresh-token";
const LOGOUT_ENDPOINT = "/api/auth/logout";
const toast = useToast();

const ERROR_MESSAGES_TO_SKIP_TOAST = [
  "Missing Access Token",
  "Access Token Expired",
];

const showErrorToastMessage = (error) => {
  if (!ERROR_MESSAGES_TO_SKIP_TOAST.includes(error)) {
    toast.error(error);
  }
};

function addQueryParams(url, params) {
  const queryParams = url.searchParams;
  for (let key in params) {
    if (Array.isArray(params[key])) {
      // Manually append each value or append an empty string for an empty array
      if (params[key].length === 0) {
        queryParams.append(key, ""); // Empty array as empty string
      } else {
        params[key].forEach((item) => queryParams.append(key, item));
      }
    } else {
      queryParams.append(key, params[key]);
    }
  }
}

export async function refreshTokens() {
  return await fetch(REFRESH_ENDPOINT);
};

/**
 * Fetches a JSON resource and returns the JSON object
 * @param {string} endpoint the resource's endpoint, *does not include the base url* ex: /api/auth/register
 * @param {dict} params the query parameters
 * @param {dict} payload the POST body parameters
 * @param {string} method the HTTP method, default to GET
 * @throws {Error} if the response is not ok or the JSON object has success: false
 * @returns
 */
export async function fetchJSON(
  endpoint,
  params,
  payload,
  method = "get",
  showErrorToast = true
) {
  const authStore = useAuthStore();
  const tokens = await authStore.getTokens();

  const options = {
    method: method.toUpperCase(),
    headers: {
      "Content-Type": "application/json",
    },
  };
  // if (method !== "get") {
  //   options.headers["Content-Type"] = "application/json";
  // }

  const currentUrl = new URL(document.URL);

  if (payload) {
    options.body = JSON.stringify(payload);
  }
  let url;
  if (endpoint.startsWith("/")) {
    url = new URL(endpoint, currentUrl);
  }
  else {
    url = new URL(endpoint);
  }

  // this pathway is only for EZee Assist API endpoints so this is safe
  if (tokens.accessToken) { 
    options.headers['Authorization'] = `Bearer ${tokens.accessToken}`;
  }

  if (params) {
    addQueryParams(url, params);
  }

  const response = await fetch(url.toString(), options);

  // Something went wrong in the query
  if (!response.ok) {
    // this is a default error message if no other message is provided
    let errTexts = [];
    if (response.statusText) errTexts.push(response.statusText);

    try {
      const responseJson = await response.json();
      console.log("Error", response.status, response.statusText, responseJson);
      if (responseJson.detail) {
        errTexts.push(responseJson.detail);
      }
    } catch (e) {
      // do nothing
    }

    let errMsg = response.status;
    if (errTexts.length) {
      errMsg = `${errMsg} - ${errTexts.join(" - ")}`;
    }
    else {
      errMsg = `${errMsg} - Unexpected Error`;
    }

    if (showErrorToast) {
      if (Array.isArray(errMsg)) {
        errMsg.forEach((error) => {
          showErrorToastMessage(error.msg);
        });
      } else {
        showErrorToastMessage(errMsg);
      }
    }

    throw new Error(errMsg);
  }

  const resJson = await response.json();
  if (resJson.hasOwnProperty("success") && resJson.success === false) {
    if (showErrorToast) {
      showErrorToastMessage(resJson.detail);
    }
    throw new Error(resJson.detail);
  }
  return resJson;
}

export function pause(ms = 1000) {
  return new Promise((resolve) => {
    setTimeout(() => {
      // console.log(`paused for: ${ms} milliseconds`);
      resolve();
    }, ms);
  });
}

export function applyOrgRedirect(response) {
  if (import.meta.env.VITE_APP_DISABLE_ORG_REDIRECT) {
    return response;
  }

  const redirectTo = response.redirect_to;
  if (!redirectTo) return response;

  const redirectToken = response.redirect_token;

  const url = new URL(window.location.href);
  const redirect = new URL(redirectTo);
  url.host = redirect.host;

  if (url.pathname.includes("auth/")) {
    url.pathname = "";
    url.searchParams.delete("redirect");
  }

  url.searchParams.append(
    "redirect_token",
    redirectToken,
  );
  console.log("Applying Org Redirect", window.location.href, url.toString());
  window.location.replace(url.toString());
}
